import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import Checkbox from '@material-ui/core/Checkbox';

import Button from '../../../../shared/_TBComponents/Button';
import TBTooltip from '../../../../shared/_TBComponents/Tooltip';
import {
  setExerFiltersGroup,
  setIsExerFiltersGroupShowing,
} from '../../ducks/sportCoachDashboardActions';
import { BoxShadow } from '../../../../shared/GlobalStyles';
import { dashboardTracker } from '../../../../shared/utils/amplitudeHelper';

const Badge = styled('span')`
  background-color: #444444;
  border-radius: 100px;
  color: #ffffff;
  display: flex;
  font-family: 'Nunito Sans';
  font-size: 11px;
  font-weight: bold;
  justify-content: center;
  position: absolute;
  right: -7px;
  top: -7px;
  width: 16px;
`;

const FilterAndButtonContainer = styled('div')`
  margin-top: 8px;
  position: relative;
`;

const FilterBottom = styled('div')`
  display: flex;
  justify-content: space-around;
  margin-top: 16px;
`;

const FilterContainer = styled('div')`
  background-color: #ffffff;
  border-radius: 4px;
  box-shadow: ${BoxShadow};
  display: flex;
  flex-direction: column;
  max-height: 420px;
  padding: 16px;
  position: absolute;
  top: 38px;
  width: 250px;
  z-index: 1;
`;

const FilterHeader = styled('div')`
  display: flex;
  font-family: 'Nunito Sans';
  font-size: 20px;
  font-weight: 700;
  justify-content: space-between;
`;

const FilterOption = styled('div')`
  align-items: center;
  display: flex;
  margin-left: -10px;

  h3 {
    font-weight: normal;
  }
`;

const Filters = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: bold;
  overflow-y: auto;
`;

const OptionText = styled('div')`
  font-family: 'Nunito Sans';
  font-weight: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ExerciseFilterGroup = () => {
  const { exerciseFiltersGroup, exercises } = useSelector(
    (state) => state.sportCoachDashboard.data,
  );
  const { isExerciseFiltersGroupShowing } = useSelector(
    (state) => state.sportCoachDashboard.ui,
  );

  const dispatch = useDispatch();

  const sortedExercises = Array.isArray(exercises) && exercises.length
    ? [...exercises].sort((a, b) => a.name.localeCompare(b.name))
    : [];

  const handleChange = (exer) => {
    // Amplitude tracking
    dashboardTracker(
      'Sport Coach Dashboard',
      'Group Analysis - Filter',
      `Exercise filter clicked - ${exer}`,
    );
    // If exercise is already in filters, remove it
    if (exerciseFiltersGroup.includes(exer)) {
      const newExercises = exerciseFiltersGroup.filter(
        (exercise) => exercise !== exer,
      );
      dispatch(setExerFiltersGroup(newExercises));
    } else {
      dispatch(setExerFiltersGroup([...exerciseFiltersGroup, exer]));
    }
  };

  const handleClear = () => {
    // Amplitude tracking
    dashboardTracker(
      'Sport Coach Dashboard',
      'Group Analysis - Filter',
      'Filters cleared',
    );
    dispatch(setExerFiltersGroup([]));
  };

  const handleClose = () => {
    // Amplitude tracking
    dashboardTracker(
      'Sport Coach Dashboard',
      'Group Analysis - Filter',
      'Filter closed',
    );
    dispatch(setIsExerFiltersGroupShowing(false));
  };

  const handleFilter = () => {
    // Amplitude tracking
    dashboardTracker(
      'Sport Coach Dashboard',
      'Group Analysis - Filter',
      'Filter clicked',
    );
    dispatch(setIsExerFiltersGroupShowing(!isExerciseFiltersGroupShowing));
  };

  // Render helpers
  const renderExerciseOption = (option) => {
    const handleOptionChange = () => {
      handleChange(option.name);
    };

    return (
      <FilterOption key={option.id}>
        <Checkbox
          checked={exerciseFiltersGroup.includes(option.name)}
          color='default'
          onChange={handleOptionChange}
        />
        {option.name.length > 20 ? (
          <TBTooltip
            id={option.name}
            displayText={option.name}
            hoverText={option.name}
            tooltipStyles={{
              backgroundColor: '#FFFFFF',
              color: '#444444',
              fontSize: '16px',
            }}
          />
        ) : (
          <OptionText>{option.name}</OptionText>
        )}
      </FilterOption>
    );
  };

  const renderFilterModal = () => {
    if (!isExerciseFiltersGroupShowing) return null;

    return (
      <FilterContainer>
        <FilterHeader>
          Exercises
          <Button
            aria-label='Close exercise filter'
            disabled={!exerciseFiltersGroup?.length}
            icon='remove'
            iconColor='#9E9E9E'
            iconOnly
            noBorder
            onClick={handleClose}
          />
        </FilterHeader>
        <Filters>{sortedExercises.map(renderExerciseOption)}</Filters>
        <FilterBottom>
          <Button name='Clear' onClick={handleClear} rounded upperCase />
          <Button
            name='Done'
            bgColor='#444444'
            txtColor='#FFFFFF'
            disabled={!exerciseFiltersGroup?.length}
            onClick={handleClose}
            rounded
            upperCase
          />
        </FilterBottom>
      </FilterContainer>
    );
  };

  return (
    <FilterAndButtonContainer>
      <Button
        name='Filter'
        icon='filter'
        onClick={handleFilter}
        rounded
        upperCase
      />
      {exerciseFiltersGroup?.length > 0 && (
        <Badge>{exerciseFiltersGroup.length}</Badge>
      )}
      {renderFilterModal()}
    </FilterAndButtonContainer>
  );
};

export default ExerciseFilterGroup;
